import {ComplexFormControl, FieldType, FormBuilder, PatternStringValidator, RequiredValidator} from "@ha/formhandler";
import type {OwnProfile} from "../../helpers/own-profile.state";

/** The values the "Mein Profil" form holds. */
export type OwnProfileFormData = {
    firstName: string,
    lastName: string,
    emailAddress: string,
    currentPassword: string,
}

const required = new RequiredValidator();

/** At least one character that is not a space: the backend trims names and refuses an empty one. */
const notBlank = new PatternStringValidator({pattern: /\S/});

/** An address with exactly one "@" and a dot after it; the backend checks the format again. */
const emailPattern = new PatternStringValidator({pattern: /^[^@\s]+@[^@\s]+\.[^@\s]+$/});

/**
 * Builds the "Mein Profil" form from the stored profile: names (required, not only spaces), e-mail (format) and the
 * current password, which is shown and needed only when the e-mail differs from the stored one.
 */
export function createOwnProfileForm(profile: OwnProfile): ComplexFormControl<OwnProfileFormData, unknown> {
    return FormBuilder.buildForm('own-profile', [
        {type: FieldType.Primitive, key: `firstName`, initialValue: profile.firstName, validations: [required, notBlank]},
        {type: FieldType.Primitive, key: `lastName`, initialValue: profile.lastName, validations: [required, notBlank]},
        {type: FieldType.Primitive, key: `emailAddress`, initialValue: profile.emailAddress, validations: [required, emailPattern]},
        {type: FieldType.Primitive, key: `currentPassword`, initialValue: ``, validations: []},
    ]);
}

/** Whether the e-mail in the form differs from the stored one, so the current password is needed. */
export function ownProfileNeedsPassword(form: ComplexFormControl<OwnProfileFormData, unknown>, saved: OwnProfile): boolean {
    return (form.getValue()?.emailAddress ?? '').trim() !== saved.emailAddress;
}

/**
 * Whether the form may be saved: something changed, no field reports a validation error, and the
 * password is filled in when the e-mail changes.
 *
 * Not `form.isValid`: on a form of fields, @ha/formhandler's `isValid` checks only validators attached
 * to the form itself, never those of its fields (the same reason as `isLoginFormSavable`).
 */
export function isOwnProfileFormSavable(form: ComplexFormControl<OwnProfileFormData, unknown>, saved: OwnProfile): boolean {
    if (!form.isDirty || form.getValidationErrors().length > 0) {
        return false;
    }

    return !ownProfileNeedsPassword(form, saved) || (form.getValue()?.currentPassword ?? '') !== '';
}
